/**
 * Numbering the rows: off by default, remembered per table.
 *
 * A POSITION IS NOT A VALUE. Everything else in the control strip decides what
 * a row SAYS — which columns are on screen, how much room a cell may take, and
 * therefore what the file carries. A row number says where the row currently
 * sits, which is a fact about the sort you happen to have on, not about the
 * thing. So it is drawn beside the table rather than added to the columns, and
 * it stays out of the CSV: a file carrying "7" in a column called # would
 * freeze one reading of one sort as though it were data somebody had found.
 *
 * What it is FOR is counting and pointing — "the fourth one down", "we had
 * nineteen of these this morning" — and, with the roster sorted by arrival,
 * seeing at a glance how far down the new ones start.
 *
 * Pure, in its own module, so CRA's jest can test it without the table.
 */

const KEY = 'jcseo.boardroom.rownums';

export function storedNumbers(table: string, fallback = false): boolean {
  try {
    const raw = window.localStorage.getItem(`${KEY}.${table}`);
    return raw === null ? fallback : raw === '1';
  } catch {
    // Private mode, or storage blocked. Off is the right default: numbers are
    // a reading aid somebody asked for, never something to impose.
    return fallback;
  }
}

export function rememberNumbers(table: string, on: boolean): void {
  try {
    window.localStorage.setItem(`${KEY}.${table}`, on ? '1' : '0');
  } catch {
    /* private mode: the default is fine */
  }
}

/**
 * The number a row wears, given where it sits in what is ON SCREEN.
 *
 * One-based, because nobody points at "row 0". Counted over the rows as drawn
 * — after the sort, after the filters — so the numbers always run 1..n down
 * the page with no gaps, rather than carrying an identity from somewhere the
 * reader cannot see.
 */
export function rowNumber(index: number): number {
  return index + 1;
}

/** How wide the gutter has to be for the longest number in the table, in `ch`.
 *  Measured rather than fixed, so a table of nine rows does not carry the
 *  indent a table of four hundred needs. */
export function gutterWidth(count: number): number {
  return Math.max(2, String(Math.max(0, count)).length + 1);
}

/**
 * Whether numbering is on offer at all.
 *
 * Not when FLIPPED: the rows are the old columns then, so a number down the
 * side counts fields rather than things, and "row 3" means a different object
 * depending on which way round the table is — the one thing a number must
 * never do. Not on an empty table either, where a gutter is furniture around
 * nothing.
 */
export function canNumber(rows: number, flipped: boolean): boolean {
  return !flipped && rows > 0;
}
